import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useT } from '../../i18n';

type Variant = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  variant: Variant;
  message: string;
}

interface ToastCtx {
  push: (message: string, variant?: Variant) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastCtx | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  // `tr`, а не `t` — ниже `t` уже занято элементом в items.map()
  const tr = useT();
  const [items, setItems] = useState<ToastItem[]>([]);
  const timeouts = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const handle = timeouts.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timeouts.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (message: string, variant: Variant = 'info') => {
      const id = ++counter;
      setItems((prev) => [...prev, { id, message, variant }]);
      const handle = setTimeout(() => remove(id), variant === 'error' ? 7000 : 4000);
      timeouts.current.set(id, handle);
    },
    [remove],
  );

  useEffect(() => {
    return () => {
      timeouts.current.forEach((h) => clearTimeout(h));
      timeouts.current.clear();
    };
  }, []);

  const value: ToastCtx = {
    push,
    success: (m) => push(m, 'success'),
    error: (m) => push(m, 'error'),
    info: (m) => push(m, 'info'),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed top-4 right-4 z-50 flex max-w-sm flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={
              'pointer-events-auto flex items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-md ' +
              (t.variant === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : t.variant === 'error'
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-slate-200 bg-white text-slate-900')
            }
          >
            <span className="flex-1 whitespace-pre-line">{t.message}</span>
            <button
              type="button"
              onClick={() => remove(t.id)}
              className="text-slate-400 hover:text-slate-600"
              aria-label={tr('toast.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
